import {
	useCallback,
	useEffect,
	useRef,
	useState,
	type RefObject,
} from 'react';
import {
	SessionCallProntuarioRecordingBadge,
	type SessionCallProntuarioBadgeHint,
} from '@/pages/professional/sessao-chamada/components/prontuario/SessionCallProntuarioRecordingBadge';
import { PRONTUARIO_BG_CLASS } from '@/pages/professional/sessao-chamada/constants/prontuarioTheme';
import { cn } from '@/lib/utils';

const DESKTOP_MIN = 22;
const DESKTOP_MAX = 58;
const MOBILE_MIN = 35;
const MOBILE_MAX = 72;
const DEFAULT_DESKTOP = 42;
const DEFAULT_MOBILE = 58;
const DIVIDER_SIZE = '1rem';
const PANEL_TRANSITION_MS = 320;
const PANEL_EASING = 'cubic-bezier(0.33, 1, 0.68, 1)';

type SessionCallResizableSplitProps = {
	callContainerRef: RefObject<HTMLDivElement>;
	callPane?: React.ReactNode;
	callOverlay?: React.ReactNode;
	prontuarioPane: React.ReactNode;
	prontuarioOpen: boolean;
	prontuarioEnabled?: boolean;
	showProntuarioBadge?: boolean;
	onOpenProntuario?: () => void;
	prontuarioBadgeHint?: SessionCallProntuarioBadgeHint;
	prontuarioBadgeHintMessage?: string;
	transcriptionActive?: boolean;
	onDismissProntuarioBadgeHint?: () => void;
};

const SessionCallResizableSplit = ({
	callContainerRef,
	callPane,
	callOverlay,
	prontuarioPane,
	prontuarioOpen,
	prontuarioEnabled = false,
	showProntuarioBadge = false,
	onOpenProntuario,
	prontuarioBadgeHint = 'hidden',
	prontuarioBadgeHintMessage,
	transcriptionActive = true,
	onDismissProntuarioBadgeHint,
}: SessionCallResizableSplitProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const prontuarioColumnRef = useRef<HTMLDivElement>(null);
	const [prontuarioSize, setProntuarioSize] = useState(DEFAULT_DESKTOP);
	const [isDesktop, setIsDesktop] = useState(
		() =>
			typeof window !== 'undefined' &&
			window.matchMedia('(min-width: 768px)').matches,
	);
	const [isResizing, setIsResizing] = useState(false);

	useEffect(() => {
		const mediaQuery = window.matchMedia('(min-width: 768px)');
		const syncLayout = () => {
			const desktop = mediaQuery.matches;
			setIsDesktop(desktop);
			setProntuarioSize(desktop ? DEFAULT_DESKTOP : DEFAULT_MOBILE);
		};

		syncLayout();
		mediaQuery.addEventListener('change', syncLayout);
		return () => mediaQuery.removeEventListener('change', syncLayout);
	}, []);

	const notifyDailyLayout = useCallback(() => {
		window.dispatchEvent(new Event('resize'));
	}, []);

	useEffect(() => {
		const callPane = callContainerRef.current?.parentElement;
		if (!callPane) return;

		const observer = new ResizeObserver(notifyDailyLayout);
		observer.observe(callPane);
		notifyDailyLayout();

		return () => observer.disconnect();
	}, [callContainerRef, notifyDailyLayout, prontuarioOpen, prontuarioSize]);

	useEffect(() => {
		const column = prontuarioColumnRef.current;
		if (!column) return;

		const handleTransitionEnd = (event: TransitionEvent) => {
			if (
				event.propertyName === 'width' ||
				event.propertyName === 'height'
			) {
				notifyDailyLayout();
			}
		};

		column.addEventListener('transitionend', handleTransitionEnd);
		return () =>
			column.removeEventListener('transitionend', handleTransitionEnd);
	}, [notifyDailyLayout, prontuarioEnabled]);

	const handleResizeStart = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			if (!prontuarioOpen) return;

			event.preventDefault();
			const container = containerRef.current;
			if (!container) return;

			const handle = event.currentTarget;
			handle.setPointerCapture(event.pointerId);
			setIsResizing(true);

			const onPointerMove = (moveEvent: PointerEvent) => {
				const bounds = container.getBoundingClientRect();
				const desktop = window.matchMedia('(min-width: 768px)').matches;

				if (desktop) {
					const fromRight = bounds.right - moveEvent.clientX;
					const percent = (fromRight / bounds.width) * 100;
					setProntuarioSize(
						Math.min(DESKTOP_MAX, Math.max(DESKTOP_MIN, percent)),
					);
					return;
				}

				const fromBottom = bounds.bottom - moveEvent.clientY;
				const percent = (fromBottom / bounds.height) * 100;
				setProntuarioSize(
					Math.min(MOBILE_MAX, Math.max(MOBILE_MIN, percent)),
				);
			};

			const onPointerUp = () => {
				setIsResizing(false);
				handle.releasePointerCapture(event.pointerId);
				handle.removeEventListener('pointermove', onPointerMove);
				handle.removeEventListener('pointerup', onPointerUp);
				handle.removeEventListener('pointercancel', onPointerUp);
				document.body.style.removeProperty('cursor');
				document.body.style.removeProperty('user-select');
			};

			document.body.style.cursor = isDesktop
				? 'col-resize'
				: 'row-resize';
			document.body.style.userSelect = 'none';
			handle.addEventListener('pointermove', onPointerMove);
			handle.addEventListener('pointerup', onPointerUp);
			handle.addEventListener('pointercancel', onPointerUp);
		},
		[isDesktop, prontuarioOpen],
	);

	const panelTransition = isResizing
		? 'none'
		: isDesktop
		  ? `width ${PANEL_TRANSITION_MS}ms ${PANEL_EASING}`
		  : `height ${PANEL_TRANSITION_MS}ms ${PANEL_EASING}`;

	return (
		<div
			ref={containerRef}
			className={cn(
				'flex min-h-0 flex-1 overflow-hidden',
				isDesktop ? 'flex-row' : 'flex-col',
			)}
		>
			<div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
				<div ref={callContainerRef} className="absolute inset-0">
					{callPane}
				</div>
				{callOverlay}
				{showProntuarioBadge && onOpenProntuario ? (
					<div
						className={cn(
							'absolute bottom-[4.75rem] right-0 z-30 md:bottom-20',
							'transition-opacity duration-200 ease-out',
							prontuarioOpen
								? 'pointer-events-none opacity-0'
								: 'opacity-100 delay-100',
						)}
					>
						<SessionCallProntuarioRecordingBadge
							onOpen={onOpenProntuario}
							hint={prontuarioBadgeHint}
							hintMessage={prontuarioBadgeHintMessage}
							transcriptionActive={transcriptionActive}
							onDismissHint={onDismissProntuarioBadgeHint}
						/>
					</div>
				) : null}
			</div>

			{prontuarioEnabled ? (
				<div
					ref={prontuarioColumnRef}
					className={cn(
						'flex shrink-0 overflow-hidden',
						isDesktop ? 'flex-row' : 'flex-col',
						PRONTUARIO_BG_CLASS,
					)}
					style={{
						width: isDesktop
							? prontuarioOpen
								? `${prontuarioSize}%`
								: '0px'
							: '100%',
						height: !isDesktop
							? prontuarioOpen
								? `${prontuarioSize}%`
								: '0px'
							: '100%',
						transition: panelTransition,
					}}
				>
					<div
						role="separator"
						aria-orientation={isDesktop ? 'vertical' : 'horizontal'}
						aria-valuenow={
							prontuarioOpen ? Math.round(prontuarioSize) : 0
						}
						aria-label="Redimensionar painéis da chamada e do prontuário"
						tabIndex={prontuarioOpen ? 0 : -1}
						onPointerDown={handleResizeStart}
						className={cn(
							'group relative z-20 flex shrink-0 touch-none items-center justify-center',
							'cursor-row-resize md:cursor-col-resize',
							'transition-opacity duration-200',
							prontuarioOpen
								? 'opacity-100'
								: 'pointer-events-none opacity-0',
						)}
						style={{
							width: isDesktop ? DIVIDER_SIZE : '100%',
							height: isDesktop ? '100%' : DIVIDER_SIZE,
						}}
					>
						<span
							className="pointer-events-none absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-slate-600 md:bottom-0 md:left-1/2 md:right-auto md:top-0 md:h-full md:w-px md:-translate-x-1/2 md:translate-y-0"
							aria-hidden
						/>
						<span
							className={cn(
								'relative z-10 rounded-full bg-slate-500 transition-colors group-hover:bg-slate-400',
								'h-1.5 w-10 md:h-14 md:w-1.5',
							)}
							aria-hidden
						/>
					</div>

					<div
						className={cn(
							'relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden',
							'transition-opacity duration-200 ease-out',
							prontuarioOpen
								? 'opacity-100 delay-75'
								: 'opacity-0',
						)}
					>
						{prontuarioPane}
					</div>
				</div>
			) : null}
		</div>
	);
};

export { SessionCallResizableSplit };
